import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "project_manager", label: "Project Manager" },
  { value: "compliance_officer", label: "Compliance Officer" },
  { value: "finance", label: "Finance" },
  { value: "dispatcher", label: "Dispatcher" },
  { value: "field_inspector", label: "Field Inspector" },
  { value: "driver", label: "Driver" },
  { value: "hauler", label: "Hauler" },
  { value: "receiver", label: "Receiver" },
  { value: "user", label: "User" },
];

/**
 * Admin dialog to edit a user's name, phone, role, and (optionally) reset
 * their password. Leaving the password field blank keeps it unchanged.
 */
export default function EditUserDialog({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "",
    phone: user.phone || "",
    role: user.role || "user",
    password: "",
    hauler_id: user.hauler_id || "",
    receiver_site_ids: user.receiver_site_ids || [],
  });
  const [haulers, setHaulers] = useState([]);
  const [sites, setSites] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  React.useEffect(() => {
    api.get("/haulers").then((r) => setHaulers(r.data)).catch(() => {});
    api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
  }, []);

  const toggleSite = (siteId) => {
    setForm((f) => {
      const cur = f.receiver_site_ids || [];
      return { ...f, receiver_site_ids: cur.includes(siteId) ? cur.filter((s) => s !== siteId) : [...cur, siteId] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.password && form.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    const payload = {
      name: form.name,
      phone: form.phone,
      role: form.role,
    };
    if (form.password) payload.password = form.password;
    if (form.role === "hauler") payload.hauler_id = form.hauler_id || null;
    if (form.role === "receiver") payload.receiver_site_ids = form.receiver_site_ids || [];
    try {
      await api.patch(`/users/${user.id}`, payload);
      toast.success(form.password ? "User updated · password reset" : "User updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="edit-user-dialog">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white border border-gray-300 w-full max-w-md">
        <div className="border-b border-gray-200 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-xl leading-none">Edit User</div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-mono mt-1">{user.email}</div>
          </div>
          <button onClick={onClose} data-testid="edit-user-close"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <F label="Name">
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="inp" data-testid="edit-name-input" />
          </F>
          <F label="Phone">
            <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="inp font-mono" data-testid="edit-phone-input" placeholder="+1 415-555-0000" />
          </F>
          <F label="Role">
            <select value={form.role} onChange={(e) => set("role", e.target.value)} className="inp" data-testid="edit-role-select">
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </F>
          {form.role === "hauler" && (
            <F label="Hauler Company">
              <select value={form.hauler_id} onChange={(e) => set("hauler_id", e.target.value)} className="inp" data-testid="edit-hauler-select">
                <option value="">— Not linked (user will see nothing) —</option>
                {haulers.map((h) => <option key={h.id} value={h.id}>{h.company_name}</option>)}
              </select>
              <div className="text-[10px] text-gray-500 mt-1 font-mono">User will only see loads on this hauler's trucks.</div>
            </F>
          )}
          {form.role === "receiver" && (
            <F label="Receiver Sites">
              <div className="border border-gray-300 max-h-40 overflow-y-auto" data-testid="edit-receiver-sites">
                {sites.length === 0 ? (
                  <div className="p-2 text-xs text-gray-500">No sites available.</div>
                ) : sites.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.receiver_site_ids.includes(s.id)}
                      onChange={() => toggleSite(s.id)}
                      data-testid={`edit-receiver-site-${s.id}`}
                    />
                    <span className="text-xs">{s.name}</span>
                  </label>
                ))}
              </div>
              <div className="text-[10px] text-gray-500 mt-1 font-mono">User will only see loads dropping at these sites.</div>
            </F>
          )}
          <div>
            <label className="label-caps block mb-1.5">
              Reset Password <span className="text-gray-400 normal-case tracking-normal">(optional)</span>
            </label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              className="inp font-mono"
              data-testid="edit-password-input"
              placeholder="Leave blank to keep current password"
              minLength={8}
              autoComplete="new-password"
            />
            <div className="text-[10px] text-gray-500 mt-1 font-mono">Min 8 characters. The user should change this on first login.</div>
          </div>
          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-2.5 text-xs uppercase tracking-widest font-semibold">Cancel</button>
            <button
              type="submit"
              disabled={busy}
              data-testid="edit-user-submit"
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2.5 text-xs uppercase tracking-widest font-semibold disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
        <style>{`.inp { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #D1D5DB; font-size: 0.875rem; } .inp:focus { outline: none; border-color: #111827; }`}</style>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="label-caps block mb-1.5">{label}</label>{children}</div>;
}
